import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserInput,
  UpdateUserInput,
  UserListQuery,
  UserResponse,
  ApiList,
  ApiSuccess,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export function useUsers(query: Partial<UserListQuery> = {}) {
  return useQuery({
    queryKey: ['users', query],
    queryFn: async (): Promise<ApiList<UserResponse>> => {
      const res = await apiClient.get<ApiList<UserResponse>>('/users', { params: query });
      return res.data;
    },
  });
}

export function useUser(id: number | undefined) {
  return useQuery({
    queryKey: ['user', id],
    enabled: id != null,
    queryFn: async (): Promise<ApiSuccess<UserResponse>> => {
      const res = await apiClient.get<ApiSuccess<UserResponse>>(`/users/${id}`);
      return res.data;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput): Promise<UserResponse> => {
      const res = await apiClient.post<ApiSuccess<UserResponse>>('/users', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateUserInput): Promise<UserResponse> => {
      const res = await apiClient.patch<ApiSuccess<UserResponse>>(`/users/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['user', id] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/users/${id}`);
    },
    onSuccess: (_d, id) => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['user', id] });
    },
  });
}
